<template>
  <Teleport v-if="showBulkActions" to="#bulk-actions">
    <BulkActions
      v-if="
        enableMultipleSelectionMode &&
        (subscriberBulkEdit || subscriberBulkDownload || enableAdminCopy)
      "
      :user-id="userId"
      :user-recipes-count="userRecipesCount"
      :recipe-search-store="recipeSearchStore"
      :recipe-tag-store="recipeTagStore"
      :daily-values-options="dailyValuesOptions"
      :enable-edit="subscriberBulkEdit"
      :enable-data-download="subscriberBulkDownload"
      :enable-admin-copy="enableAdminCopy"
      :per-page="perPage"
      :delete-confirms="deleteConfirms"
    />
    <button
      v-else
      class="button is-info is-outlined"
      data-subscriber-feature
      data-pitch="Subscribe to select multiple recipes for bulk downloads, menu labeling, tagging, and archiving."
    >
      <span class="icon-text">
        <span class="icon">
          <i class="fas fa-ellipsis-h"></i>
        </span>
        <span>Actions</span>
      </span>
    </button>
  </Teleport>
  <RecipeSelector
    :recipe-search-store="recipeSearchStore"
    :recipe-tag-store="recipeTagStore"
    :selection-mode="enableMultipleSelectionMode"
    :multiple-selection-mode="enableMultipleSelectionMode"
    :enable-archiving="enableArchiving"
    :allow-locking="allowLocking"
    :delete-confirms="deleteConfirms"
    :show-filters="showFilters"
    :load="false"
    :user-has-archived-recipes="userHasArchivedRecipes"
    :allow-inline-tag-edits="allowInlineTagEdits"
    show-recipe-actions-column
  />
</template>

<script setup>import { computed as _computed } from 'vue';

  import RecipeSelector from "./recipe-selector.vue";
  import BulkActions from "./bulk-manage-recipes/bulk-actions.vue";

  import { useRecipeSearchStore } from "./stores/recipe-search-store.js";
  import { useRecipeTagStore } from "./stores/recipe-tag-store.js";
  import { RECIPE_DASHBOARD_SEARCH_STORE } from "./stores/constants.js";
  import useSearchStoreHistory from "./lib/use-search-store-history.js";
  import useViewLoadTracking from "./lib/use-view-load-tracking.js";

  import { recipeFilterUserPath } from "~/routes";

  import { onBeforeMount } from "vue";
  import { useEventListener } from "@vueuse/core";

  const props = defineProps({
    userId: { type: String, required: true },
    perPage: { type: Number, default: 10 },
    userRecipesCount: { type: Number, required: true },
    userHasArchivedRecipes: { type: Boolean, required: true },
    userIsSubscribed: { type: Boolean, required: false },
    enableArchiving: { type: Boolean, default: true },
    allowLocking: { type: Boolean, default: false },

    // Important: if enableEdit/enableDataDownload, outer page must have #bulk-actions element (otherwise, expect some JS errors).
    enableEdit: { type: Boolean, default: false },
    enableDataDownload: { type: Boolean, default: false },
    enableAdminCopy: { type: Boolean, default: false },
    allowInlineTagEdits: { type: Boolean, default: true },

    // Pass down from Ruby: `"daily-values-options": daily_values_flat_json`
    dailyValuesOptions: { type: Array, required: true },

    deleteConfirms: { type: Object, required: true },
  });

  const recipeSearchStore = useRecipeSearchStore(RECIPE_DASHBOARD_SEARCH_STORE);
  const recipeTagStore = useRecipeTagStore();

  useViewLoadTracking(() => recipeSearchStore.loaded);

  onBeforeMount(() => {
    recipeSearchStore.setUserId(props.userId);
    recipeSearchStore.setSearchEndpoint(recipeFilterUserPath(props.userId));
    recipeSearchStore.assignInitial({ perPage: props.perPage }, { patch: true });

    recipeTagStore.setUserId(props.userId);
    recipeTagStore.loadAllTags();

    const loadFromUrl = useSearchStoreHistory(recipeSearchStore, "recipe_", ["user_id"]);
    useEventListener(window, "popstate", () => {
      loadFromUrl();
    });
    loadFromUrl();
  });

  const showBulkActions = _computed(() => props.userRecipesCount > 1);

  const enableMultipleSelectionMode = _computed(() =>
    Boolean((props.userIsSubscribed || props.enableAdminCopy) && showBulkActions.value)
  );

  // Admin can? bypass makes enableEdit/enableDataDownload true while unsubscribed; re-gate to subscriber-only.
  const subscriberBulkEdit = _computed(() => Boolean(props.enableEdit && props.userIsSubscribed));
  const subscriberBulkDownload = _computed(() =>
    Boolean(props.enableDataDownload && props.userIsSubscribed)
  );

  const showFilters = _computed(() =>
    Boolean(
      props.userRecipesCount > props.perPage ||
        props.userHasArchivedRecipes ||
        recipeSearchStore.knownArchivedRecipesExist ||
        recipeSearchStore.hasQuery ||
        recipeSearchStore.hasAdvancedFilters
    )
  );
</script>
